import axios from "axios";
import { endOfWeek, format, startOfWeek } from "date-fns";
import { groupBy } from "lodash";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { DateParam, useQueryParam } from "use-query-params";

import CenterLoader from "../components/CenterLoader";
import { UserContext } from "../contexts/UserContext";
import DepartmentReps from "./DepartmentRep";
import SettlementTable from "./SettlementTable";
import { LoadingBubbles, LoadingSpinner, Root } from "./styles";
import { CloserAndDepartment, CloserFile, DepartmentForDashboard } from "./types";

import MonthSelector from "./MonthSelector";

const startOfCurrentWeek = startOfWeek(new Date(), { weekStartsOn: 0 });
const endOfCurrentWeek = endOfWeek(new Date(), { weekStartsOn: 0 });

const defaultStartDate = startOfCurrentWeek;
const defaultEndDate = endOfCurrentWeek;

const departments: DepartmentForDashboard[] = [
  { name: "National", repsClassName: "columnWrap_National" },
  { name: "TX", repsClassName: "columnWrap_TX" },
  { name: "NJ/PA", repsClassName: "columnWrap_NJ_PA" },
  { name: "NY", repsClassName: "columnWrap_NY" }
];

const SettlementsDashboard = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [closerFiles, setCloserFiles] = useState<CloserFile[]>([]);
  const [startDate, setStartDate] = useQueryParam("startDate", DateParam);
  const [endDate, setEndDate] = useQueryParam("endDate", DateParam);

  const isDefaultDates = !startDate && !endDate;
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCloserAndDepartment, setSelectedCloserAndDepartment] = useState<CloserAndDepartment>({
    department: "",
    closer: ""
  });

  const filesGroupedByState = groupBy(closerFiles, "department");
  const tableRows = closerFiles.filter(
    (f) => f.department == selectedCloserAndDepartment.department && f.closer == selectedCloserAndDepartment.closer
  );

  useEffect(() => {
    const authorizedPageViewers = [
      "bfreylicher@madisoncres.com",
      "ymeister@madisontitle.com",
      "jshonek@madisontitle.com",
      "mbecker@madisoncres.com",
      "sdavidowitz@madisoncres.com",
      "agoldstein@madisoncres.com",
      "afriedman@madisoncres.com",
      "tnewman@madisoncres.com"
    ];

    if (user != null && !authorizedPageViewers.includes(user.email.toLowerCase())) {
      navigate("/unauthorized");
    }
  }, [user]);

  const getSettlementDashboardData = async () => {
    try {
      setIsLoading(true);
      const from = format(startDate || defaultStartDate, "yyyy-MM-dd");
      const to = format(endDate || defaultEndDate, "yyyy-MM-dd");
      const { data } = await axios.get<CloserFile[]>(`/api/settlementsDashboard/GetCloserFiles?from=${from}&to=${to}`);
      const sortedData = [...data].sort((a, b) => {
        const nameA = (a?.closer || "").toLowerCase();
        const nameB = (b?.closer || "").toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
      setCloserFiles(sortedData);
    } catch (error) {
      console.error("Failed to fetch settlement data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getSettlementDashboardData();
  }, [startDate, endDate]);

  if (!user) return <CenterLoader isOpen />;
  const uniqueClosers = new Set(closerFiles.map((file) => file.closer));
  return (
    <Root>
      <MonthSelector />
      <div className="gridWrap">
        <div className="smallCard">
          <h2 className="cardTitle">Total Closers</h2>
          {isDefaultDates && <div className="thisWeek">This Week</div>}
          <div className="cardValue">
            {isLoading ? <LoadingBubbles /> : new Set(closerFiles.map((file) => file.closer)).size}
          </div>
        </div>
        <div className="smallCard smallCardGreen">
          <h2 className="cardTitle">Total Settlements</h2>
          {isDefaultDates && <div className="thisWeek">This Week</div>}
          <div className="cardValue">{isLoading ? <LoadingBubbles /> : closerFiles.length || 0}</div>
        </div>

        <div className="closers">
          <div className="closersHeaderWrap">
            <h2 className="closersHeader">Closer Teams</h2>
            <div className="legendWrap">
              {departments.map((department) => (
                <div key={department.name} className="legendItem">
                  <div>{department.name}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="repsWrap">
            {isLoading ? (
              <div className="loadingWrap">
                <LoadingSpinner />
              </div>
            ) : (
              departments.map((department) => (
                <DepartmentReps
                  key={department.name}
                  department={department}
                  filesForDeparment={filesGroupedByState[department.name]}
                  selectedCloserAndDepartment={selectedCloserAndDepartment}
                  setSelectedCloserAndDepartment={setSelectedCloserAndDepartment}
                />
              ))
            )}
          </div>
        </div>
        <SettlementTable
          rows={tableRows}
          noSelection={selectedCloserAndDepartment.closer == "" || selectedCloserAndDepartment.department == ""}
        />
      </div>
    </Root>
  );
};

export default SettlementsDashboard;
